import { lazy } from 'react'

/**
 * Módulo: Tareas
 *
 * Sistema nervioso de CoWork. Conecta detección de problemas/oportunidades
 * en cualquier módulo con las personas que deben actuar.
 *
 * Subsistemas: Tareas, Señales, Motor de Reglas, IA, Métricas, Calendario.
 * Absorbe el antiguo módulo "Accionables".
 *
 * NOTA: el id interno sigue siendo 'asignaciones' por compatibilidad con
 * vistas.js (modulosVisibles), permisos y referencias en código. El cambio
 * a "Tareas" es a nivel etiqueta visible (nombre + label en sidebar).
 * Si en algún momento querés renombrar el id, hace falta migrar también
 * los docs de /vistas en Firestore que tienen 'asignaciones' hardcodeado.
 */
export default {
  id: 'asignaciones',
  nombre: 'Tareas',
  descripcion: 'Tareas, señales, reglas, IA y métricas. Asigná, delegá y hacé seguimiento.',
  icono: 'check-square',
  tipo: 'core',
  version: '2.0.0',

  routes: [
    // F4 (ADR 044): "Mis tareas" se plegó a "Mi agenda" — /tareas redirige.
    { path: 'tareas', component: lazy(() => import('./pages/TareasRedirect')) },
    { path: 'señales', component: lazy(() => import('./pages/SeñalesPage')) },
    { path: 'asignaciones', component: lazy(() => import('./pages/AsignacionesPage')) },
    { path: 'asignaciones/:id', component: lazy(() => import('./pages/AsignacionDetallePage')) },
    { path: 'reglas-negocio', component: lazy(() => import('./pages/ReglasPage')) },
    // Plan IA Paso 7.8 — los jobs del motor IA se consolidaron en /ia/motor.
    { path: 'prompts-ia', component: lazy(() => import('./pages/PromptsIaRedirect')) },
    { path: 'metricas', component: lazy(() => import('./pages/MetricasPage')) },
    { path: 'calendario', component: lazy(() => import('./pages/CalendarioPage')) },
  ],

  // #87 — `permisoModulo` filtra item-by-item según el rol.
  // F4 (ADR 044, decisión Mariano 6-jul): "Mis tareas" se plegó a Mi agenda
  // (toggle Mías/Asignadas por mí) y "Todas las tareas" dejó el sidebar — la
  // gestión vive DENTRO de cada empresa (solapa Tareas): con 20 empresas ×
  // 20 usuarios el pizarrón global era inservible. Las rutas /tareas (redirect)
  // y /asignaciones(+/:id) siguen vivas para deep-links y notificaciones.
  menuItems: [
    { label: 'Señales', path: '/señales', icon: 'zap', permisoModulo: 'señales' },
    // "Calendario" se mudó al grupo GESTIÓN (gestion/config.js) el 6-jul —
    // ahora es calendario DEL USUARIO y vive junto a Mis actividades. La ruta
    // /calendario sigue acá (routes), solo se movió el ítem del menú.
    { label: 'Reglas', path: '/reglas-negocio', icon: 'sliders', permisoModulo: 'reglas' },
    // Plan IA Paso 7.8 — "Prompts IA" (jobs del motor) se movió a /ia → Motor.
    { label: 'Métricas', path: '/metricas', icon: 'bar-chart-2', permisoModulo: 'metricas' },
  ],

  features: [
    'tareas',
    'señales',
    'asignaciones',
    'calendario',
    'reglas-negocio',
    'prompts-ia',
    'metricas',
    'delegacion',
    'escalamiento',
  ],

  recursos: [
    { id: 'asignaciones', label: 'Asignaciones' },
    { id: 'reglas', label: 'Reglas de negocio' },
  ],

  // permisoModulos (#58): se deriva del rol.
  // Incluye el código de cada ítem del menú cuya ruta monta este módulo: lo exige `src/core/lib/menuRutaPermiso.guard.test.js`.
  permisoModulos: ['accionables', 'asignaciones', 'señales', 'reglas', 'metricas', 'calendario'],

}
